import { useEffect, useState } from 'react'
/* Importação NOMEADA, não `import * as`.
   O `import * as Icones` funcionava e levou o bundle de 431 kB para
   1.374 kB — a biblioteca inteira entrou para desenhar catorze ícones.
   O `lucide-react` não faz tree-shaking do namespace inteiro, e nem o
   `tsc` nem o build acusam: só o número no fim do build denuncia. */
import {
  House, CalendarDays, ListChecks, UserRoundCheck, Users, Receipt, Wallet,
  ArrowLeftRight, ChartNoAxesColumn, Stethoscope, FileText,
  MessageCircle, ShieldCheck, Settings, Search,
  CircleUser, Building2, DoorOpen, ClipboardList, Clock,
  type LucideIcon,
} from 'lucide-react'
import { GRUPOS, DESTINOS, ir, type Destino } from '../lib/navegacao'
import { Marca } from './Marca'
import './barra-lateral.css'

/**
 * A barra lateral do app.
 *
 * Substitui as seis abas horizontais. O motivo não é gosto: aba
 * horizontal cabe seis ou sete antes de estourar a largura, e nenhuma
 * das referências de painel clínico que o Lucas trouxe usa abas — todas
 * usam rail. Com 24 telas, a aba era o teto que empurrava tudo para
 * dentro de "Clínica".
 *
 * **O item ativo é branco elevado, não turquesa.** Nas referências o
 * ativo é sempre a INVERSÃO do fundo do rail: branco sobre cinza no
 * MedCore, verde-claro sobre preto no untitledui. Ele sobe em vez de
 * acender, e a cor da marca fica livre para o conteúdo — que é onde a
 * pessoa olha depois da primeira semana.
 */

const CHAVE = 'clinuma:rail-recolhido'
const APERTADO = 1100

/* A busca, a identidade e as ações de conta SAÍRAM daqui para o
   `Cabecalho`. Não foi arrumação: `docs/design-composicao.md` §1 diz
   que as referências separam a faixa de sessão do cabeçalho da tela, e
   que juntar as duas foi o que fez o nome da clínica brigar com o
   menu. O rail volta a ser uma coisa só — navegação. */
export function BarraLateral({ atual, pode }: {
  atual: Destino | null
  pode: (escopo: string) => boolean
}) {
  /* A escolha da pessoa vale; o aperto da janela manda quando a janela
     é pequena demais para o rail aberto caber sem comer a grade. */
  const [escolha, setEscolha] = useState(() => localStorage.getItem(CHAVE) === '1')
  const [estreito, setEstreito] = useState(() => window.innerWidth < APERTADO)

  useEffect(() => {
    const medir = () => setEstreito(window.innerWidth < APERTADO)
    window.addEventListener('resize', medir)
    return () => window.removeEventListener('resize', medir)
  }, [])

  const recolhido = escolha || estreito

  const alternar = () => {
    const novo = !escolha
    setEscolha(novo)
    localStorage.setItem(CHAVE, novo ? '1' : '0')
  }

  const visiveis = DESTINOS.filter((d) => !d.oculto && (!d.escopo || pode(d.escopo)))

  return (
    <nav className={`rail${recolhido ? ' rail--estreito' : ''}`} aria-label="Seções do sistema">
      <div className="rail__topo">
        <Marca tamanho={24} comNome={!recolhido} />
        {/* Quando a janela é que apertou, esconder o botão evita
            prometer uma escolha que a largura vai desfazer. */}
        {!estreito && (
          <button className="rail__dobrar" onClick={alternar}
                  aria-label={recolhido ? 'Expandir o menu' : 'Recolher o menu'}
                  aria-expanded={!recolhido}>
            {recolhido ? '⇥' : '⇤'}
          </button>
        )}
      </div>


      <div className="rail__lista">
        {GRUPOS.map((g) => {
          const itens = visiveis.filter((d) => d.grupo === g.id)
          // Grupo sem item some inteiro, título junto: uma seção vazia
          // com nome só anuncia o que a pessoa não pode ver.
          if (!itens.length) return null
          return (
            <div className="rail__grupo" key={g.id}>
              {recolhido
                ? <hr className="rail__sep" />
                : <h2 className="rail__grupo-titulo">{g.titulo}</h2>}
              {itens.map((d) => {
                const ativo = atual?.caminho === d.caminho
                return (
                  <button key={d.caminho}
                          className={`rail__item${ativo ? ' is-ativa' : ''}`}
                          onClick={() => ir(d.caminho)}
                          aria-current={ativo ? 'page' : undefined}
                          title={recolhido ? d.rotulo : undefined}>
                    <Icone nome={d.icone} />
                    {!recolhido && <span className="rail__rotulo">{d.rotulo}</span>}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>

    </nav>
  )
}

/**
 * O mapa nome → componente.
 *
 * `navegacao.ts` guarda só a STRING, para o mapa de destinos continuar
 * sendo dado puro — sem `import` de React, ele pode ser lido por teste,
 * por script e por qualquer coisa que não renderize. A ponte entre a
 * string e o componente mora aqui, e é explícita justamente para o
 * bundler enxergar quais catorze ícones entram.
 */
const ICONES: Record<string, LucideIcon> = {
  House, CalendarDays, ListChecks, UserRoundCheck, Users, Receipt, Wallet,
  ArrowLeftRight, ChartNoAxesColumn, Stethoscope, FileText,
  MessageCircle, ShieldCheck, Settings, Search, CircleUser,
  Building2, DoorOpen, ClipboardList, Clock,
}

function Icone({ nome }: { nome: string }) {
  const C = ICONES[nome]
  if (!C) return <span className="rail__glifo" aria-hidden="true">•</span>
  return (
    <span className="rail__glifo" aria-hidden="true">
      <C size={17} strokeWidth={1.9} />
    </span>
  )
}
